import { useState } from 'react';
import { useLanguage, useT } from '../i18n/i18n';
import Comments from './Comments';
import { useLearner } from '../learners/LearnerContext';
import { GOAL_STATUS_LABEL, SOURCE_LABEL, copyGoal, deleteGoal, type Goal } from './api';
import { formatDay } from './GoalDates';
import GoalEditor, { GAS_LEVELS } from './GoalEditor';
import TargetCard from './TargetCard';
import TargetEditor from './TargetEditor';
import './extras.css';

export type AskConfirm = (c: { title: string; message: string; confirmLabel: string; run: () => Promise<unknown> }) => void;

export default function GoalCard({ learnerId, goal, onChanged, ask }: {
  learnerId: string; goal: Goal; onChanged: () => void; ask: AskConfirm;
}) {
  const t = useT();
  const { lang } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [comments, setComments] = useState(false);
  const { learners } = useLearner();
  const [copying, setCopying] = useState(false);
  const [copyTo, setCopyTo] = useState('');
  const [copyBusy, setCopyBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const g = goal;
  const gas = GAS_LEVELS.filter((l) => g.gas?.[l.key]);
  const others = learners.length >= 2 ? learners.filter((l) => l.id !== learnerId) : [];

  async function copy() {
    const to = others.find((l) => l.id === copyTo);
    if (!to) return;
    setCopyBusy(true);
    setCopyError(null);
    setCopied(null);
    try {
      await copyGoal(learnerId, g.id, to.id);
      setCopied(t('Copied to {name}.', { name: to.name }));
    } catch (e) {
      setCopyError(e instanceof Error ? e.message : t('That could not be copied just now.'));
    } finally {
      setCopyBusy(false);
    }
  }

  return (
    <article className="card planner-goal">
      {editing ? (
        <GoalEditor
          learnerId={learnerId}
          goal={g}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }}
          onDelete={() => ask({
            title: t('Delete this goal?'),
            message: t('The goal, its targets and their activities will be removed. This cannot be undone.'),
            confirmLabel: t('Delete goal'),
            run: () => deleteGoal(learnerId, g.id),
          })}
        />
      ) : (
        <>
          <div className="planner-goal-head">
            <h3 className="planner-goal-statement">{g.statement}</h3>
            <span className={`pill planner-goal-status is-${g.status.toLowerCase()}`}>{t(GOAL_STATUS_LABEL[g.status])}</span>
          </div>
          <dl className="planner-goal-meta">
            <div><dt>{t('Where it comes from')}</dt><dd>{t(SOURCE_LABEL[g.source])}</dd></div>
            {g.byDate && <div><dt>{t('By when')}</dt><dd>{formatDay(g.byDate, lang)}</dd></div>}
            {g.baseline && <div><dt>{t('What happens now')}</dt><dd>{g.baseline}</dd></div>}
          </dl>
          {gas.length > 0 && (
            <div className="planner-goal-gas">
              <h4>{t('Goal-attainment levels')}</h4>
              <ul>
                {gas.map((l) => (
                  <li key={l.key}>
                    <span className="planner-goal-gas-level">{l.sign}</span>
                    <span>
                      <span className="planner-goal-gas-name">{t(l.label)}</span>
                      <span>{g.gas?.[l.key]}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {g.openComments > 0 && (
            <p className="planner-goal-waiting">
              {g.openComments === 1 ? t('1 question waiting') : t('{n} questions waiting', { n: g.openComments })}
            </p>
          )}
          <div className="toolbar">
            <button type="button" className="btn secondary" onClick={() => setEditing(true)}>{t('Edit')}</button>
            <button type="button" className="btn secondary" onClick={() => setAdding(true)} disabled={adding}>{t('Add a target')}</button>
            <button type="button" className="btn ghost" aria-expanded={comments} onClick={() => setComments(!comments)}>
              {comments ? t('Hide comments') : t('Comments')}
            </button>
            {others.length > 0 && (
              <button type="button" className="btn ghost" aria-expanded={copying} onClick={() => setCopying(!copying)}>{t('Copy to another learner')}</button>
            )}
          </div>
          {copying && (
            <div className="planner-tpl-copy">
              <div className="field">
                <label htmlFor={`goal-copy-${g.id}`}>{t('Copy to')}</label>
                <select id={`goal-copy-${g.id}`} value={copyTo} onChange={(e) => { setCopyTo(e.target.value); setCopied(null); }}>
                  <option value="">{t('Choose a learner')}</option>
                  {others.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <p className="field-hint">{t('Programs that belong to this learner are copied as activities away from the app.')}</p>
              {copyError && <p className="error" role="alert">{copyError}</p>}
              {copied && <p className="planner-tpl-done" role="status">{copied}</p>}
              <div className="toolbar">
                <button type="button" className="btn" disabled={!copyTo || copyBusy} onClick={() => void copy()}>{copyBusy ? t('Copying…') : t('Copy goal')}</button>
                <button type="button" className="btn secondary" disabled={copyBusy} onClick={() => { setCopying(false); setCopied(null); setCopyError(null); }}>{t('Close')}</button>
              </div>
            </div>
          )}
          {comments && <Comments learnerId={learnerId} subjectType="GOAL" subjectId={g.id} onChange={onChanged} />}
        </>
      )}
      <section className="planner-goal-targets" aria-label={t('Targets')}>
        <h4>{t('Targets')}</h4>
        {g.targets.length === 0 && !adding && <p className="muted">{t('No targets yet. A target is a step towards this goal, reviewed each term.')}</p>}
        {g.targets.length > 0 && (
          <ul className="planner-target-list">
            {g.targets.map((x) => <TargetCard key={x.id} learnerId={learnerId} target={x} onChanged={onChanged} ask={ask} />)}
          </ul>
        )}
        {adding && (
          <div className="planner-target is-editing">
            <TargetEditor learnerId={learnerId} goalId={g.id} target={null} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onChanged(); }} />
          </div>
        )}
      </section>
    </article>
  );
}
